import React, { useContext } from 'react'

import { get } from 'lodash'
import { Controller, useFormContext } from 'react-hook-form'

import { FormWarningsContext } from './formWarnings'

// Convert field-level validators (`(value) => error | undefined`, or an array) to RHF's `(value) => true | message`.
// GOTCHA: RHF ignores field-level rules when the form has a resolver, so mixing withForm({ validate }) with per-field `validate` requires folding those checks into the form-level validator.
const toRhfValidate = (validate: any) => (value: any) => {
  const fns = Array.isArray(validate) ? validate : [validate]
  for (const fn of fns) {
    const error = fn?.(value)
    if (error) return error
  }
  return true
}

// `normalize` receives the extracted value, not the raw event, so mirror RHF's event-value extraction first.
const eventValue = (e: any) =>
  e && typeof e === 'object' && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e

// Binds a shared input component to an RHF Controller, emitting the `field={{ input, meta }}` shape input components consume.
export default (DecoratedComponent: React.ComponentType<any>) => {
  const FormField = (props: any) => {
    const { control, getValues } = useFormContext()
    const warnings = useContext(FormWarningsContext)
    // Keep normalize/format/validate out of the props spread — form-adapter concerns, not DOM props.
    const { normalize, format, validate, ...rest } = props
    const { name } = rest

    return (
      <Controller
        control={control}
        name={name}
        {...(validate ? { rules: { validate: toRhfValidate(validate) } } : {})}
        render={({ field, fieldState, formState }) => (
          <DecoratedComponent
            {...rest}
            field={{
              input: {
                name: field.name,
                onBlur: field.onBlur,
                // redux-form `normalize(value, previousValue, allValues)`; 4th arg not threaded, no normalize → RHF's native onChange.
                onChange: normalize
                  ? (arg: any) => field.onChange(normalize(eventValue(arg), field.value, getValues()))
                  : field.onChange,
                // redux-form format(): stored value -> displayed value; `?? ''` keeps the input controlled when format returns null.
                value: format ? (format(field.value) ?? '') : (field.value ?? ''),
              },
              // Treat a submitted form as fully touched so errors surface (redux-form touched every field on failed submit).
              meta: {
                error: fieldState.error?.message,
                touched: fieldState.isTouched || formState.isSubmitted,
                warning: get(warnings, name),
              },
            }}
          />
        )}
      />
    )
  }

  return FormField
}
